import { cn } from '@shadcn/lib/utils';
import { Card } from '@shadcn/ui/card';
import { differenceInYears } from 'date-fns';
import type { TFunction } from 'i18next';
import { LucideCircleX } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '../../atoms/tooltip/tooltip';

interface IDemographic {
  readonly icon?: React.ReactElement;
  readonly label: string;
  readonly value: string | number;
  readonly labelPrefix?: string;
}

interface IDemographicsBarProps extends React.ComponentPropsWithoutRef<'div'> {
  readonly name?: string;
  readonly patientId: { readonly label: string; readonly value?: string };
  readonly patientIdLookup?: string;
  readonly piSystemId: string;
  readonly healthcareLookupPerformed?: boolean;
  readonly dateOfBirth?: Date;
  readonly gender?: string;
  readonly sex?: string;
  readonly ethnicity?: string;
}

type BuildDemographicsParams = {
  age?: number;
  ethnicity?: string;
  formattedDateOfBirth?: string;
  gender?: string;
  name?: string;
  patientIdLookup: IDemographicsBarProps['patientIdLookup'];
  patientId: IDemographicsBarProps['patientId'];
  piSystemId: string;
  healthcareLookupPerformed: boolean;
  sex?: string;
  t: TFunction;
};

type GetPatientIdDemographicParams = {
  eprIdLabel: string;
  patientId: IDemographicsBarProps['patientId'];
  patientIdLookup?: string;
  piSystemId: string;
  notFoundText: string;
  healthcareLookupPerformed: boolean;
  tooltipText?: string;
  mismatchText?: string;
};

function getPatientIdDemographic({
  eprIdLabel,
  patientId,
  patientIdLookup,
  piSystemId,
  notFoundText,
  healthcareLookupPerformed,
  tooltipText,
  mismatchText,
}: GetPatientIdDemographicParams): IDemographic {
  const patientIdToShow = healthcareLookupPerformed ? patientIdLookup || notFoundText : piSystemId;
  const idLabelToShow = healthcareLookupPerformed ? patientId.label : eprIdLabel;

  return {
    ...(patientIdLookup &&
      patientId.value &&
      patientIdLookup !== patientId.value && {
        icon: (
          <Tooltip content={tooltipText}>
            <LucideCircleX stroke="red" />
          </Tooltip>
        ),
        labelPrefix: `${mismatchText}`,
      }),
    label: idLabelToShow,
    value: patientIdToShow,
  };
}

function buildDemographics({
  age,
  ethnicity,
  formattedDateOfBirth,
  gender,
  name,
  patientId,
  patientIdLookup,
  piSystemId,
  healthcareLookupPerformed,
  sex,
  t,
}: BuildDemographicsParams): IDemographic[] {
  const incompletePlaceholder = t('ui-components.demographics-bar.incompletePlaceholder');
  return [
    {
      label: t('ui-components.demographics-bar.demographic.name'),
      value: name ?? incompletePlaceholder,
    },
    getPatientIdDemographic({
      eprIdLabel: t('ui-components.demographics-bar.demographic.eprIdLabel'),
      healthcareLookupPerformed,
      mismatchText: t('ui-components.demographics-bar.patient-id-mismatch.text'),
      notFoundText: t('ui-components.demographics-bar.demographic.notFoundText'),
      patientId,
      patientIdLookup,
      piSystemId,
      tooltipText: t('ui-components.demographics-bar.patient-id-mismatch.label'),
    }),
    { label: t('ui-components.demographics-bar.demographic.age'), value: age ?? incompletePlaceholder },
    {
      label: t('ui-components.demographics-bar.demographic.date-of-birth'),
      value: formattedDateOfBirth ?? incompletePlaceholder,
    },
    {
      label: t('ui-components.demographics-bar.demographic.gender'),
      value: gender ?? incompletePlaceholder,
    },
    {
      label: t('ui-components.demographics-bar.demographic.sex'),
      value: sex ?? incompletePlaceholder,
    },
    {
      label: t('ui-components.demographics-bar.demographic.ethnicity'),
      value: ethnicity ?? incompletePlaceholder,
    },
  ] satisfies IDemographic[];
}

function DemographicItem({ label, value, icon: Icon, labelPrefix }: IDemographic) {
  return (
    <div className="flex gap-1 items-center text-md text-foreground">
      {Icon && <span>{Icon}</span>}
      {labelPrefix && (
        <span aria-label="Demographics labelPrefix" className="font-semibold whitespace-nowrap">
          {labelPrefix}
        </span>
      )}
      <span aria-label="Demographics label" className="font-semibold whitespace-nowrap">
        {label}
      </span>
      <span aria-label="Demographics value">{value || '-'}</span>
    </div>
  );
}

function DemographicsBar({
  className,
  name,
  patientId,
  patientIdLookup,
  piSystemId,
  healthcareLookupPerformed = false,
  dateOfBirth,
  gender,
  sex,
  ethnicity,
  ...props
}: IDemographicsBarProps) {
  const { t } = useTranslation();
  const age = dateOfBirth ? differenceInYears(new Date(), dateOfBirth) : undefined;
  const formattedDateOfBirth = dateOfBirth ? t('date.short', { date: dateOfBirth }) : undefined;

  const demographics = buildDemographics({
    age,
    ethnicity,
    formattedDateOfBirth,
    gender,
    healthcareLookupPerformed,
    name,
    patientId,
    patientIdLookup,
    piSystemId,
    sex,
    t,
  });

  return (
    <Card
      className={cn('bg-white shadow-none border border-1 border-neutral-700 rounded-lg py-0 px-0', className)}
      {...props}
    >
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 min-h-[37px]">
        {demographics.map(({ label, value, icon, labelPrefix }) => (
          <DemographicItem icon={icon} key={label} label={label} labelPrefix={labelPrefix} value={value} />
        ))}
      </div>
    </Card>
  );
}

export { DemographicsBar, type IDemographicsBarProps };
